import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { CampaignReportData } from '../../../types/campaignReport';
import { formatPercent } from '../../../lib/reportFormatters';
import { Badge } from '../../ui/badge';
import { Star, Smile } from 'lucide-react';

interface FeedbackTabProps {
  data: CampaignReportData;
}

export function FeedbackTab({ data }: FeedbackTabProps) {
  return (
    <div className="space-y-8">
      {/* Customer Feedback Table */}
      <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
        <CardHeader>
          <CardTitle className="text-xl font-bold" style={{ color: '#183df7' }}>
            8️⃣ Customer Feedback
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-semibold">Metric</TableHead>
                <TableHead className="text-right font-semibold">Score</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="font-medium">Overall Experience Score</TableCell>
                <TableCell className="text-right font-semibold" style={{ color: '#183df7' }}>
                  {data.feedback.overallExperienceScore} / 5
                </TableCell>
              </TableRow>
              {data.feedback.easeOfPurchaseScore && (
                <TableRow>
                  <TableCell className="font-medium">Ease of Purchase</TableCell>
                  <TableCell className="text-right font-semibold" style={{ color: '#183df7' }}>
                    {data.feedback.easeOfPurchaseScore} / 5
                  </TableCell>
                </TableRow>
              )}
              {data.feedback.giftAppealRating && (
                <TableRow>
                  <TableCell className="font-medium">Gift Appeal Rating</TableCell>
                  <TableCell className="text-right font-semibold" style={{ color: '#183df7' }}>
                    {data.feedback.giftAppealRating} / 5
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell className="font-medium">Would Recommend</TableCell>
                <TableCell className="text-right font-semibold" style={{ color: '#183df7' }}>
                  {formatPercent(data.feedback.wouldRecommend)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Common Feedback Themes */}
      {data.feedback.commonThemes && data.feedback.commonThemes.length > 0 && (
        <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
          <CardHeader>
            <CardTitle className="text-lg font-semibold" style={{ color: '#183df7' }}>
              Common Feedback Themes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.feedback.commonThemes.map((theme, index) => (
                <div 
                  key={index}
                  className="p-4 bg-blue-50 border border-blue-200 rounded-lg"
                  style={{ boxShadow: '0 2px 4px rgba(24, 61, 247, 0.1)' }}
                >
                  <p className="text-sm text-gray-700 italic">"{theme}"</p>
                </div>
              ))}
            </div>
            <div className="mt-6 text-center">
              <Badge className="text-base px-4 py-2 bg-green-500 text-white flex items-center gap-2 justify-center mx-auto w-fit">
                <Smile className="h-5 w-5" />
                Highly Positive Sentiment
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rating Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="text-center" style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
          <CardContent className="pt-6">
            <Star className="w-8 h-8 mx-auto mb-2" style={{ color: '#183df7' }} />
            <p className="text-sm text-muted-foreground mb-1">Overall Experience</p>
            <p className="text-2xl font-bold" style={{ color: '#183df7' }}>
              {data.feedback.overallExperienceScore} / 5
            </p>
          </CardContent>
        </Card>
        {data.feedback.easeOfPurchaseScore && (
          <Card className="text-center" style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
            <CardContent className="pt-6">
              <Star className="w-8 h-8 mx-auto mb-2" style={{ color: '#183df7' }} />
              <p className="text-sm text-muted-foreground mb-1">Ease of Purchase</p>
              <p className="text-2xl font-bold" style={{ color: '#183df7' }}>
                {data.feedback.easeOfPurchaseScore} / 5
              </p>
            </CardContent>
          </Card>
        )}
        {data.feedback.giftAppealRating && (
          <Card className="text-center" style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
            <CardContent className="pt-6">
              <Star className="w-8 h-8 mx-auto mb-2" style={{ color: '#183df7' }} />
              <p className="text-sm text-muted-foreground mb-1">Gift Appeal</p>
              <p className="text-2xl font-bold" style={{ color: '#183df7' }}>
                {data.feedback.giftAppealRating} / 5
              </p>
            </CardContent>
          </Card>
        )}
        <Card className="text-center" style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
          <CardContent className="pt-6">
            <Star className="w-8 h-8 mx-auto mb-2" style={{ color: '#183df7' }} />
            <p className="text-sm text-muted-foreground mb-1">Would Recommend</p>
            <p className="text-2xl font-bold" style={{ color: '#183df7' }}>
              {formatPercent(data.feedback.wouldRecommend)}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
